import { FormattedMessage, IntlShape } from "react-intl";
import { PriceManagement } from "../../../modification/model";
import React, { useCallback, useState } from "react";
import { RATE_TYPE } from "../../../witadmin/Common/constants";
import { format, isBefore, isWithinInterval, startOfDay, subDays } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleRight, faInfo, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-bootstrap";
import { PromoAmountObject, Row } from "./types";
import { messages } from "./messages";
import {
  GridAppliedPriceInput,
  GridAriPrice,
  GridDate,
  GridDiscountInput,
  GridFinalPrice,
  GridPromos,
  GridRestrictions,
  GridSeparator,
  ModalTableHeaderWrapper,
  PromosStyledTable,
  PromosStyledTableWrapper,
  RestrictionsStyledTable,
  RestrictionsTableWrapper,
  StyledFontAwesomeIcon,
  StyledFontAwesomeIconWrapper,
  StyledModal,
  StyledModalHeader,
} from "./styles";

export default function GridRow({
  row,
  rows,
  index,
  intl,
  selectedOption,
  formData,
  handleChange,
  finalPrices,
  handleDeletePrice,
  defaultCurrency,
  regex,
  readOnlyPrices,
  invalidDates,
}: {
  row: Row;
  rows: Row[];
  index: number;
  selectedOption: { label: string; value: string } | undefined;
  intl: IntlShape;
  handleDeletePrice: (index: number, type: string) => void;
  handleChange: (
    value: number | string,
    date: string,
    type: string,
    regex: RegExp | undefined
  ) => void;
  finalPrices: { date: string; finalPrice: number | undefined }[];
  formData: PriceManagement;
  defaultCurrency: string;
  regex: RegExp;
  readOnlyPrices?: boolean;
  invalidDates?: Set<string>;
}) {
  const [modalOpenPromos, setModalOpenPromos] = useState<number | undefined>(undefined);
  const [modalOpenRestrictions, setModalOpenRestrictions] = useState<number | undefined>(undefined);
  const rowDate = startOfDay(new Date(row.date));
  const checkIn = startOfDay(new Date(formData.checkIn));
  const checkOut = startOfDay(new Date(formData.checkOut));

  const handleClosepromosModalData = useCallback(() => {
    setModalOpenPromos(undefined);
  }, [setModalOpenPromos]);

  const handleOpenpromosModalData = useCallback(
    (index: number) => {
      setModalOpenPromos(index);
    },
    [setModalOpenPromos]
  );

  const handleCloseModalRestrictions = useCallback(() => {
    setModalOpenRestrictions(undefined);
  }, [setModalOpenRestrictions]);

  const handleOpenModalRestrictions = useCallback(
    (index: number) => {
      setModalOpenRestrictions(index);
    },
    [setModalOpenRestrictions]
  );

  const promos =
    !!row?.promos?.length &&
    `${row?.promos[0].ticker} (${
      row?.promos[0].type === RATE_TYPE.PERCENTAGE
        ? row?.promos[0].amount + "%"
        : (row?.promos[0].amount as PromoAmountObject).amount +
          (row?.promos[0].amount as PromoAmountObject).currency
    })...`;
  const promosModalData = row?.promos
    ?.filter(promo => promo.amount)
    .map(promo => {
      let promoValue = "";
      if (promo.applyEveryNumberOfUnits && promo.discountedUnits) {
        promoValue = `${promo.applyEveryNumberOfUnits}x${
          Number(promo.applyEveryNumberOfUnits) - Number(promo.discountedUnits)
        }`;
      } else if (promo.type === RATE_TYPE.PERCENTAGE) {
        promoValue = promo.amount + "%";
      } else {
        promoValue = `${
          (promo.amount as PromoAmountObject).amount
        } ${defaultCurrency}/${intl.formatMessage(messages.day)}`;
      }

      let group = promo.group;

      if (typeof promo.group === "string") {
        group = promo.group[promo.group.length - 1];
      }

      return {
        ticker: promo.ticker,
        value: promoValue,
        multiOpenBooking: promo.multiOpenBooking,
        openTravelStartDate: promo.openTravelStartDate,
        openTravelEndDate: promo.openTravelEndDate,
        openBookingStartDate: promo.openBookingStartDate,
        openBookingEndDate: promo.openBookingEndDate,
        minStay: promo.minStay,
        maxStay: promo.maxStay,
        maxNotice: promo.maxNotice,
        minNotice: promo.minNotice,
        group,
      };
    });
  let restrictions =
    !!Object.entries(row.restrictions || {}).length &&
    `${Object.entries(row.restrictions || {})[0][0]}(${
      Object.entries(row.restrictions || {})[0][1]
    })...`;

  const restrictionsModalData = row.restrictions;
  const canEdit =
    row.appliedPrice ||
    row.appliedPrice === 0 ||
    (rows[index + 1] && rows[index + 1].appliedPrice) ||
    (rows[index + 1] && rows[index + 1].appliedPrice === 0) ||
    (rows[index - 1] && rows[index - 1].appliedPrice) ||
    (rows[index - 1] && rows[index - 1].appliedPrice === 0);

  const validInterval = isBefore(checkIn, checkOut);

  const canDeleteTop =
    validInterval &&
    row.appliedPrice &&
    rows[index - 1] &&
    (rows[index - 1].appliedPrice === undefined || rows[index - 1].appliedPrice === null) &&
    !isWithinInterval(rowDate, {
      start: checkIn,
      end: subDays(checkOut, 1),
    });

  const canDeleteBottom =
    validInterval &&
    row.appliedPrice &&
    rows[index + 1] &&
    (rows[index + 1].appliedPrice === undefined || rows[index + 1].appliedPrice === null) &&
    !isWithinInterval(rowDate, {
      start: checkIn,
      end: subDays(checkOut, 1),
    });

  const originalSelection =
    validInterval &&
    isWithinInterval(rowDate, {
      start: checkIn,
      end: subDays(checkOut, 1),
    });

  const dateError = !!invalidDates?.has(row.date);

  return (
    <React.Fragment key={row.date}>
      <GridDate bold={originalSelection} error={dateError}>
        {row.date}
      </GridDate>
      <GridAriPrice bold={originalSelection}>
        <span>{row.ariPrice}</span>
        {(row.appliedPrice === undefined || row.appliedPrice === null) &&
          canEdit &&
          !readOnlyPrices &&
          typeof row.ariPrice === "number" && (
            <StyledFontAwesomeIconWrapper
              className={"styled-font-awesome-icon-wrapper"}
              onClick={() => handleChange(row.ariPrice, row.date, "appliedPrice", undefined)}>
              <FontAwesomeIcon icon={faAngleDoubleRight} size={"xs"} />
            </StyledFontAwesomeIconWrapper>
          )}
      </GridAriPrice>
      <GridAppliedPriceInput
        debounceTimeout={500}
        disabled={!canEdit || !!readOnlyPrices}
        value={row.appliedPrice || row.appliedPrice === 0 ? String(row.appliedPrice) : ""}
        onChange={event =>
          handleChange(Number(event.target.value), row.date, "appliedPrice", regex)
        }
        placeholder={
          (row.appliedPrice === undefined && canEdit && intl.formatMessage(messages.insertPrice)) ||
          ""
        }
        bold={originalSelection}></GridAppliedPriceInput>
      <GridDiscountInput
        disabled={!canEdit}
        value={row.discount || ""}
        onChange={event => handleChange(event.target.value, row.date, "discount", regex)}
        bold={originalSelection}></GridDiscountInput>
      <GridFinalPrice bold={originalSelection}>
        <span> {finalPrices.find(it => it.date === row.date)?.finalPrice}</span>
        {!!canDeleteTop && !readOnlyPrices && (
          <StyledFontAwesomeIcon onClick={() => handleDeletePrice(index, "top")} icon={faTimes} />
        )}
        {!!canDeleteBottom && !readOnlyPrices && (
          <StyledFontAwesomeIcon
            onClick={() => handleDeletePrice(index, "bottom")}
            icon={faTimes}
          />
        )}
      </GridFinalPrice>
      <GridSeparator />
      <GridRestrictions bold={originalSelection}>
        <span>{restrictions}</span>
        <StyledFontAwesomeIconWrapper
          onClick={() => handleOpenModalRestrictions(index)}
          className={"styled-font-awesome-icon-wrapper"}>
          <FontAwesomeIcon icon={faInfo} size={"xs"} />
        </StyledFontAwesomeIconWrapper>
      </GridRestrictions>
      <GridPromos bold={originalSelection}>
        <span>{promos}</span>{" "}
        {promos && (
          <StyledFontAwesomeIconWrapper
            onClick={() => handleOpenpromosModalData(index)}
            className={"styled-font-awesome-icon-wrapper"}>
            <FontAwesomeIcon icon={faInfo} size={"xs"} />
          </StyledFontAwesomeIconWrapper>
        )}
      </GridPromos>
      <StyledModal
        centered
        show={modalOpenPromos === index}
        onHide={() => handleClosepromosModalData()}
        dialogClassName={"styled-modal"}>
        <StyledModalHeader closeButton>
          <FormattedMessage {...messages.promosHeader} />
        </StyledModalHeader>
        <Modal.Body>
          <ModalTableHeaderWrapper>
            <div>
              <FormattedMessage {...messages.day} />: {row?.date}
            </div>
            <div>
              <FormattedMessage {...messages.room} />: {selectedOption?.value}
            </div>
            <div>{selectedOption?.label}</div>
          </ModalTableHeaderWrapper>
          <PromosStyledTableWrapper>
            <PromosStyledTable>
              <tr>
                <th>
                  <FormattedMessage {...messages.name} />
                </th>
                <th>
                  <FormattedMessage {...messages.type} />
                </th>
                <th>
                  <FormattedMessage {...messages.travelWindow} />
                </th>
                <th>
                  <FormattedMessage {...messages.bookingWindow} />
                </th>
                <th>
                  <FormattedMessage {...messages.minStay} />
                </th>
                <th>
                  <FormattedMessage {...messages.maxStay} />
                </th>
                <th>
                  <FormattedMessage {...messages.minAdv} />
                </th>
                <th>
                  <FormattedMessage {...messages.maxAdv} />
                </th>
                <th>Group</th>
              </tr>
              {promosModalData?.map(promo => {
                const openTravelStartDate = promo?.openTravelStartDate
                  ? format(new Date(promo?.openTravelStartDate), "yyyy/MM/dd")
                  : "";
                const openTravelEndDate = promo?.openTravelEndDate
                  ? format(new Date(promo?.openTravelEndDate), "yyyy/MM/dd")
                  : "";
                const openBookingStartDate = promo?.openBookingStartDate
                  ? format(new Date(promo?.openBookingStartDate), "yyyy/MM/dd")
                  : "";
                const openBookingEndDate = promo?.openBookingEndDate
                  ? format(new Date(promo?.openBookingEndDate), "yyyy/MM/dd")
                  : "";
                return (
                  <tr>
                    <td>{promo.ticker || "-"}</td>
                    <td>{promo.value || "-"}</td>
                    <td>
                      {openTravelStartDate && openTravelEndDate
                        ? `${openTravelStartDate} - ${openTravelEndDate}`
                        : "-"}
                    </td>
                    <td>
                      {promo.multiOpenBooking ? (
                        <FormattedMessage {...messages.multiple} />
                      ) : openBookingStartDate && openBookingEndDate ? (
                        `${openBookingStartDate} - ${openBookingEndDate}`
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{promo.minStay || "-"}</td>
                    <td>{promo.maxStay || "-"}</td>
                    <td>{promo.minNotice || "-"}</td>
                    <td>{promo.maxNotice || "-"}</td>
                    <td>{promo.group || "-"}</td>
                  </tr>
                );
              })}
            </PromosStyledTable>
          </PromosStyledTableWrapper>
        </Modal.Body>
      </StyledModal>
      <StyledModal
        width={"30vw"}
        centered
        show={modalOpenRestrictions === index}
        onHide={() => handleCloseModalRestrictions()}
        dialogClassName={"styled-modal"}>
        <StyledModalHeader closeButton>
          <FormattedMessage {...messages.restrictionsHeader} />
        </StyledModalHeader>
        <Modal.Body>
          <ModalTableHeaderWrapper>
            <div>
              <FormattedMessage {...messages.day} />: {row?.date}
            </div>
            <div>
              <FormattedMessage {...messages.room} />: {selectedOption?.value}
            </div>
            <div>{selectedOption?.label}</div>
          </ModalTableHeaderWrapper>
          <RestrictionsTableWrapper>
            <RestrictionsStyledTable>
              <tr>
                <td>
                  <FormattedMessage {...messages.availability} />
                </td>
                <td>{restrictionsModalData?.availability || "-"}</td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage {...messages.stop} />
                </td>
                <td>{restrictionsModalData?.closed ? "X" : "-"}</td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage {...messages.minStay} />
                </td>
                <td>{restrictionsModalData?.minStay || "-"}</td>
              </tr>
            </RestrictionsStyledTable>
            <RestrictionsStyledTable>
              <tr>
                <td>
                  <FormattedMessage {...messages.maxStay} />
                </td>
                <td>{restrictionsModalData?.maxStay || "-"}</td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage {...messages.minAdv} />
                </td>
                <td>{restrictionsModalData?.minNotice || "0"}</td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage {...messages.maxAdv} />
                </td>
                <td>{restrictionsModalData?.maxNotice || "0"}</td>
              </tr>
            </RestrictionsStyledTable>
          </RestrictionsTableWrapper>
        </Modal.Body>
      </StyledModal>
    </React.Fragment>
  );
}
